import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  fetchJobBriefs,
  fetchCandidates,
  createCandidate,
  updateCandidate,
  deleteCandidate,
  createJobBrief,
  updateJobBrief,
  deleteJobBrief,
  fetchUserProfile
} from '../../../services/api';

import SideNav from '../SideNav';
import { Modal, Button, Form, Dropdown } from 'react-bootstrap';
import { BsThreeDotsVertical } from 'react-icons/bs';

const STATUS_COLUMNS = [
  { key: 'not_available', label: <>Not Available |<br /> Not Shortlisted</> },
  { key: 'available', label: 'Available' },
  { key: 'interview', label: 'Interview' },
  { key: 'test_task', label: <>Test Task |<br /> Final Round</> },
  { key: 'approved', label: 'Approved' }
];

const OutstaffBoard = () => {
  const [candidates, setCandidates] = useState([]);
  const [jobBriefs, setJobBriefs] = useState([]);

  const [showCandidateModal, setShowCandidateModal] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [newCandidate, setNewCandidate] = useState({ name: '', rate: '', role: '', cv_link: '' });

  const [showBriefModal, setShowBriefModal] = useState(false);
  const [editingBrief, setEditingBrief] = useState(null);
  const [newBrief, setNewBrief] = useState({ title: '', description: '', brief_link: '' });

  const [candidateToDelete, setCandidateToDelete] = useState(null);
  const [showConfirmDeleteCandidateModal, setShowConfirmDeleteCandidateModal] = useState(false);

  const [briefToDelete, setBriefToDelete] = useState(null);
  const [showConfirmDeleteJobBriefModal, setShowConfirmDeleteJobBriefModal] = useState(false);

  const [userProfile, setUserProfile] = useState(null);
  const token = localStorage.getItem('authToken');
  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      try {
        const profile = await fetchUserProfile(token);
        setUserProfile(profile);

        const briefs = await fetchJobBriefs();
        setJobBriefs(briefs);

        const candidateList = await fetchCandidates();
        setCandidates(candidateList);
      } catch (err) {
        console.error('Error loading data:', err);
      }
    };

    if (token) {
      loadData();
    }
  }, [token]);

  const handleAddCandidate = async () => {
    try {
      const created = await createCandidate(newCandidate);
      setCandidates(prev => [...prev, created]);
      resetCandidateModal();
    } catch (err) {
      console.error('Error adding candidate:', err);
      alert('Error adding candidate');
    }
  };

  const handleUpdateCandidate = async () => {
    if (!editingCandidate) return;

    try {
      const updated = await updateCandidate(editingCandidate.id, newCandidate);
      setCandidates(prev => prev.map(c => (c.id === updated.id ? updated : c)));
      resetCandidateModal();
    } catch (err) {
      console.error('Error updating candidate:', err);
      alert('Error updating candidate');
    }
  };

  const confirmDeleteCandidate = (candidate) => {
    setCandidateToDelete(candidate);
    setShowConfirmDeleteCandidateModal(true);
  };

  const handleDeleteCandidate = async () => {
    if (!candidateToDelete) return;
    try {
      await deleteCandidate(candidateToDelete.id);
      setCandidates(prev => prev.filter(c => c.id !== candidateToDelete.id));
      setShowConfirmDeleteCandidateModal(false);
      setCandidateToDelete(null);
      resetCandidateModal();
    } catch (err) {
      console.error('Error deleting candidate:', err);
      alert('Error deleting candidate');
    }
  };

  const handleAddJobBrief = async () => {
    try {
      const created = await createJobBrief(newBrief);
      setJobBriefs(prev => [created, ...prev]);
      resetBriefModal();
    } catch (err) {
      console.error('Error adding job brief:', err);
      alert('Error: ' + (err?.response?.data?.detail || err.message));
    }
  };

  const handleUpdateJobBrief = async () => {
    const updated = await updateJobBrief(editingBrief.id, newBrief);
    setJobBriefs(prev =>
      prev.map(brief => (brief.id === editingBrief.id ? updated : brief))
    );
    resetBriefModal();
  };

  const confirmDeleteJobBrief = (brief) => {
    setBriefToDelete(brief);
    setShowConfirmDeleteJobBriefModal(true);
  };

  const handleDeleteJobBrief = async () => {
    if (!briefToDelete) return;
    try {
      await deleteJobBrief(briefToDelete.id);
      setJobBriefs(prev => prev.filter(b => b.id !== briefToDelete.id));
      setShowConfirmDeleteJobBriefModal(false);
      setBriefToDelete(null);
      resetBriefModal();
    } catch (err) {
      console.error('Error deleting job brief:', err);
      alert('Error deleting job brief');
    }
  };

  const resetCandidateModal = () => {
    setShowCandidateModal(false);
    setEditingCandidate(null);
    setNewCandidate({ name: '', rate: '', role: '', cv_link: '' });
  };

  const resetBriefModal = () => {
    setShowBriefModal(false);
    setEditingBrief(null);
    setNewBrief({ title: '', description: '', brief_link: '' });
  };

  const handleDragStart = (e, candidateId) => {
    e.dataTransfer.setData('candidateId', candidateId);
  };

  const handleDrop = async (e, newStatus) => {
    const candidateId = e.dataTransfer.getData('candidateId');
    const candidate = candidates.find(c => c.id === parseInt(candidateId));
    if (!candidate) return alert('Candidate not found');
    try {
      const updated = await updateCandidate(candidateId, {
        ...candidate,
        status: newStatus
      });
      setCandidates(prev => prev.map(c => (c.id === updated.id ? updated : c)));
    } catch (err) {
      console.error('Error updating candidate:', err);
      alert('Error updating status');
    }
  };

  const filteredCandidates = status => candidates.filter(c => c.status === status);

  const handleLogout = () => navigate('/logout');

  return (
    <div className="container-fluid dashboard-background">
      <div className="row">
        <SideNav userProfile={userProfile} handleLogout={handleLogout} />

        <div className="col-md-10 outstaff-board mt-3" style={{ minHeight: '100vh' }}>
          {/* Header */}
          <div className="text-center mb-4 d-flex align-items-center justify-content-center">
            <img src="https://res.cloudinary.com/dnbbm9vzi/image/upload/v1747769920/logo_oq3zwy.png" alt="Logo" style={{ width: '50px', height: '50px', marginRight: '10px' }} />
            <h1 className="d-inline mb-0 fw-bold">Digis Customer Portal</h1>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3 className='fw-bold mt-3'>Outstaff Candidates</h3>
            <div className="d-flex gap-2">
              <Button onClick={() => setShowCandidateModal(true)} className="fw-bold d-flex align-items-center">
                <span className="me-2 text-white">+</span>Add Candidate
              </Button>
              <Button onClick={() => setShowBriefModal(true)} className="fw-bold d-flex align-items-center">
                <span className="me-2 text-white">+</span>Add Job Brief
              </Button>
            </div>
          </div>

          {/* Job Briefs */}
          {Array.isArray(jobBriefs) && jobBriefs.length > 0 && (
            <div className="d-flex flex-row flex-wrap gap-3">
              {jobBriefs.map((brief, index) => (
                <div key={index} className="alert d-flex justify-content-between" style={{
                  maxWidth: '25%', marginLeft: 10, backgroundColor: '#3F8CFF', color: '#333',
                  borderRadius: '0.45rem', padding: '1rem'
                }}>
                  <div className='text-white fw-bold'>
                    <strong>Job Brief</strong>
                    <div><strong>Title:</strong> {brief.title}</div>
                    <div><strong>Engagement:</strong> {brief.description}</div>
                    <a href={brief.brief_link} target="_blank" rel="noreferrer" style={{ color: 'white', textDecoration: 'underline' }}>{brief.title}</a>
                  </div>
                  <Dropdown>
                    <Dropdown.Toggle variant="light" size="sm" className="no-arrow">
                      <BsThreeDotsVertical />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => {
                        setEditingBrief(brief);
                        setNewBrief(brief);
                        setShowBriefModal(true);
                      }}>Edit Job Brief</Dropdown.Item>
                      <Dropdown.Item onClick={() => confirmDeleteJobBrief(brief)}>Delete Job Brief</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              ))}
            </div>
          )}

          {/* Candidate Columns */}
          <div className="board-columns d-flex mt-3">
            {STATUS_COLUMNS.map(({ key, label }) => (
              <div key={key} className="column border p-2 me-2 flex-grow-1"
                onDragOver={e => e.preventDefault()} onDrop={e => handleDrop(e, key)}
                style={{
                  backgroundColor: 'white', border: '2px solid #dee2e6',
                  borderRadius: '8px', minWidth: '220px'
                }}>
                <h5 className="text-center fw-bold mb-3" style={{
                  height: '60px', backgroundImage: 'repeating-linear-gradient(to right, #ccc 0, #ccc 10px, transparent 10px, transparent 25px)',
                  backgroundSize: '35px 2px', backgroundRepeat: 'repeat-x', backgroundPosition: 'bottom'
                }}>{label}</h5>
                {filteredCandidates(key).map(c => (
                  <div key={c.id} className="card mb-2 p-2" draggable
                    onDragStart={e => handleDragStart(e, c.id)}
                    style={{
                      backgroundImage: 'url(https://res.cloudinary.com/dnbbm9vzi/image/upload/v1749666438/Main_Page_soqhly.png)',
                      backgroundSize: 'cover', border: '1px solid #ccc',
                      borderRadius: '8px', minHeight: '120px'
                    }}>
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <h6 className='fw-bold mb-2'>{c.name}</h6>
                        <div className='text-dark mb-1'>{c.role}</div>
                        <p>Rate: ${c.rate}</p>
                        <a href={c.cv_link} target="_blank" rel="noreferrer">View Profile</a>
                      </div>
                      <Dropdown>
                        <Dropdown.Toggle variant="light" size="sm" className="no-arrow">
                          <BsThreeDotsVertical />
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => {
                            setEditingCandidate(c);
                            setNewCandidate(c);
                            setShowCandidateModal(true);
                          }}>Edit Candidate</Dropdown.Item>
                          <Dropdown.Item onClick={() => confirmDeleteCandidate(c)}>Delete Candidate</Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Candidate Modal */}
          <Modal show={showCandidateModal} onHide={resetCandidateModal}>
            <Modal.Header closeButton>
              <Modal.Title className="text-center w-100 fw-bold">{editingCandidate ? 'Edit Candidate' : 'Add Candidate'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form>
                {['name', 'rate', 'role', 'cv_link'].map(field => (
                  <Form.Group className="mb-3" key={field}>
                    <Form.Label>
                      {field === 'cv_link'
                        ? 'CV Link'
                        : field.charAt(0).toUpperCase() + field.slice(1)
                      }
                    </Form.Label>
                    <Form.Control
                      type={field === 'rate' ? 'number' : 'text'}
                      value={newCandidate[field]}
                      onChange={e => setNewCandidate({ ...newCandidate, [field]: e.target.value })}
                    />
                  </Form.Group>
                ))}
              </Form>

            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
              <Button variant="dark" onClick={resetCandidateModal} className="fw-bold">Cancel</Button>
              {editingCandidate ? (
                <>
                  <Button variant="danger" onClick={() => confirmDeleteCandidate(editingCandidate)} className="fw-bold">Delete</Button>
                  <Button variant="primary" onClick={handleUpdateCandidate} className="fw-bold">Update</Button>
                </>
              ) : (
                <Button variant="primary" onClick={handleAddCandidate} className="fw-bold">Create</Button>
              )}
            </Modal.Footer>
          </Modal>

          {/* Job Brief Modal */}
          <Modal show={showBriefModal} onHide={resetBriefModal}>
            <Modal.Header closeButton>
              <Modal.Title className="w-100 text-center fw-bold">{editingBrief ? 'Edit Job Brief' : 'Add Job Brief'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form>
                {['title', 'description', 'brief_link'].map(field => (
                  <Form.Group className="mb-3" key={field}>
                    <Form.Label>
                      {field === 'description'
                        ? 'Engagement'
                        : field === 'brief_link'
                          ? 'Brief Link'
                          : field.charAt(0).toUpperCase() + field.slice(1)}
                    </Form.Label>
                    <Form.Control type="text" value={newBrief[field]} onChange={e => setNewBrief({ ...newBrief, [field]: e.target.value })} />
                  </Form.Group>
                ))}
              </Form>
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
              <Button variant="dark" onClick={resetBriefModal} className="fw-bold">Cancel</Button>
              {editingBrief ? (
                <>
                  <Button variant="danger" onClick={() => confirmDeleteJobBrief(editingBrief)} className="fw-bold">Delete</Button>
                  <Button variant="primary" onClick={handleUpdateJobBrief} className="fw-bold">Update</Button>
                </>
              ) : (
                <Button variant="primary" onClick={handleAddJobBrief} className="fw-bold">Create</Button>
              )}
            </Modal.Footer>
          </Modal>

          {/* Delete Candidate Confirmation Modal */}
          <Modal show={showConfirmDeleteCandidateModal} onHide={() => setShowConfirmDeleteCandidateModal(false)}>
            <Modal.Header closeButton className="justify-content-center">
              <Modal.Title className="text-dark fw-bold text-center w-100">
                Confirm Deletion
              </Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <div className="text-center">
                Are you sure you want to delete <strong>{candidateToDelete?.name}</strong>?
              </div>
            </Modal.Body>

            <Modal.Footer className="d-flex justify-content-between">
              <Button variant="dark fw-bold" onClick={() => setShowConfirmDeleteCandidateModal(false)}>Cancel</Button>
              <Button variant="danger fw-bold" onClick={handleDeleteCandidate}>Delete</Button>
            </Modal.Footer>
          </Modal>


          {/* Delete Job Brief Confirmation Modal */}
          <Modal show={showConfirmDeleteJobBriefModal} onHide={() => setShowConfirmDeleteJobBriefModal(false)}>
            <Modal.Header closeButton className="justify-content-center">
              <Modal.Title className="text-dark fw-bold text-center w-100">
                Confirm Deletion
              </Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <div className="text-center">
                Are you sure you want to delete job brief <strong>{briefToDelete?.title}</strong>?
              </div>
            </Modal.Body>

            <Modal.Footer className="d-flex justify-content-between">
              <Button variant="dark fw-bold" onClick={() => setShowConfirmDeleteJobBriefModal(false)}>Cancel</Button>
              <Button variant="danger fw-bold" onClick={handleDeleteJobBrief}>Delete</Button>
            </Modal.Footer>
          </Modal>

        </div>
      </div>
    </div>
  );
};

export default OutstaffBoard;

